import { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import H4 from '@base/H4/H4';
import Link from '@base/Link/Link';
import P1 from '@base/P1/P1';
import { useSelector } from 'react-redux';
import { Spacings } from '@styles/index';

const GoalGroupNavContainer = styled.ul`
  grid-column: span 6;
  position: sticky;
  top: 100px;
  height: calc(100vh - 100px);
  padding-inline-start: 0;
  overflow-y: auto;
  @media (max-width: 70.95em) {
    grid-column: span 7;
  }
  @media (max-width: 47.95em) {
    height: 100%;
    position: relative;
    padding: 0;
    top: 0;
    margin-top: ${({ $nomarginTop }) => ($nomarginTop ? '0px' : '66px')};
  }
`;

const GoalGroupItem = styled.li`
  padding-bottom: ${Spacings.SPACING_3B};
  list-style-type: none;
  cursor: pointer;
  @media (max-width: 47.95em) {
    box-shadow: inset 0px -1px 0px #f0f4f7;
    height: 56px;
    align-items: center;
    padding: 0 16px;
    display: ${({ $hide }) => ($hide ? 'none' : 'flex')};
  }
`;

const SelectedGoalGroup = styled(H4)`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.fontGrey};
  @media (max-width: 47.95em) {
    font-weight: 400;
  }
`;

const GoalGroupName = styled(P1)`
  line-height: 24px;
  @media (max-width: 47.95em) {
    color: #3c4852;
    font-size: 16px;
  }
`;

const Count = styled.div`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    width: 20px;
    height: 25px;
    background: #f0f4f7;
    border-radius: 4px;
    color: #627984;
    text-align: center;
    line-height: 25px;
  }
`;

const StyledLink = styled(Link)`
  @media (max-width: 47.95em) {
    display: flex;
    width: 100%;
    justify-content: space-between;
    height: 100%;
    align-items: center;
  }
`;

const GoalGroupNav = ({
  goalGroups,
  currentRefIndex,
  setCurrentRefIndex,
  handleChooseGoal,
  $nomarginTop
}) => {
  const isMobile = useSelector(({ helper }) => helper.isMobile);
  const theme = useContext(ThemeContext);
  const gotoGoalGroupListing = (e, goalGroup, index) => {
    e.preventDefault();
    if (isMobile) {
      handleChooseGoal(goalGroup);
      return;
    }
    setCurrentRefIndex(index);
    const scrollDistance =
      document.getElementById(goalGroup.uid).offsetTop - 100; // 100 - header height + shadows apprx
    window.scrollTo({
      top: scrollDistance,
      behaviour: 'smooth'
    });
  };

  return (
    <GoalGroupNavContainer $nomarginTop={$nomarginTop}>
      {goalGroups.map((goalGroup, i) => {
        return (
          <GoalGroupItem
            key={goalGroup.uid}
            $hide={goalGroup.uid === 'my-goals'}
          >
            <StyledLink
              href={`#${goalGroup.uid}`}
              onClick={(e) => gotoGoalGroupListing(e, goalGroup, i)}
            >
              {currentRefIndex === i ? (
                <SelectedGoalGroup>{goalGroup.name}</SelectedGoalGroup>
              ) : (
                <GoalGroupName color={theme.colors.fontLightGrey}>
                  {goalGroup.name}
                </GoalGroupName>
              )}
              <Count>{goalGroup.children.length}</Count>
            </StyledLink>
          </GoalGroupItem>
        );
      })}
    </GoalGroupNavContainer>
  );
};

export default GoalGroupNav;

GoalGroupNav.propTypes = {
  goalGroups: PropTypes.objectOf({}).isRequired,
  currentRefIndex: PropTypes.number.isRequired,
  setCurrentRefIndex: PropTypes.func.isRequired,
  handleChooseGoal: PropTypes.func.isRequired,
  $nomarginTop: PropTypes.bool.isRequired
};
